<script setup lang="ts">
    import { ref, computed, watch } from 'vue'

    import { KendoGrid, KendoGridField } from '@Kendo/Components/KendoGrid'
    import { ellipsis, isNullOrWhitespace, strCompareLowerCase } from '@common/Common'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        propOpened: boolean
        propGrid?: KendoGrid | null
    }>()

    const emit = defineEmits<{
        (e: 'close-dialog'): void
    }>()

    // ---------------- local state ----------------

    // visible columns model
    const Fields = ref<FieldVM[]>([])
    const Sections = ref<SectionVM[]>([])
    const SearchPhrase = ref<string | null>(null)

    // ---------------- computed ----------------

    const opened = computed(() => props.propOpened)
    const grid = computed(() => props.propGrid ?? null)

    const SelectedFields = computed<FieldVM[]>(() =>
        Fields.value.filter(f => f.IsSelected)
    )

    // ---------------- watcher (opened) ----------------

    watch(opened, (val) =>
    {
        if (val !== true || !grid.value) return

        const columns = grid.value.GetOptions().GetColumns()
            .filter(c => !isNullOrWhitespace(c.title))
            .filter(c => c.command == null)
            .filter(c => c.hidden !== true)
            .map(c => c.field)

        Fields.value = grid.value.GetFields()
            .filter(f => f.HasColumn)
            .map(f => new FieldVM(f))

        Fields.value
            .filter(f => columns.includes(f.Id))
            .forEach(f => { f.IsSelected = true })

        Sections.value = Fields.value
            .map(f => f.Section)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .sort((a, b) => strCompareLowerCase(a, b))
            .map(name => new SectionVM(name))
    })

    // ---------------- actions ----------------

    function ToggleColumnCommand(fieldId: string)
    {
        const fieldVM = Fields.value.find(f => f.Id === fieldId)
        if (fieldVM) fieldVM.IsSelected = !fieldVM.IsSelected
    }

    function AddColumnCommand(fieldId: string)
    {
        const fieldVM = Fields.value.find(f => f.Id === fieldId)
        if (fieldVM) fieldVM.IsSelected = true
    }

    function RemoveColumnCommand(fieldId: string)
    {
        const fieldVM = Fields.value.find(f => f.Id === fieldId)
        if (fieldVM) fieldVM.IsSelected = false
    }

    function confirm()
    {
        if (!grid.value) return
        grid.value.SetColumns(SelectedFields.value.map(f => f.Id))
        close()
    }

    function close()
    {
        emit('close-dialog')
    }

    // ---------------- helper classes ----------------

    class SectionVM
    {
        Name: string

        constructor(name: string)
        {
            this.Name = name
        }

        get Fields(): FieldVM[]
        {
            let fields = Fields.value.filter(f => f.Section === this.Name)

            if (!isNullOrWhitespace(SearchPhrase.value))
            {
                const phrase = SearchPhrase.value!.toLowerCase()
                fields = fields.filter(f => f.Title.toLowerCase().includes(phrase))
            }

            return fields.sort((a, b) => strCompareLowerCase(a.Title, b.Title))
        }
    }

    class FieldVM
    {
        Id: string
        Title: string
        Section: string
        IsSelected = false
        TitleEllipsis: string

        constructor(field: KendoGridField)
        {
            this.Id = field.FieldId
            this.Title = field.ColumnTitle
            this.Section = field.Section
            this.TitleEllipsis = ellipsis(this.Title, 52)
        }
    }
</script>


<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Choose which columns you see</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="row">

                            <div class="col-md-6">
                                <div class="input-group">
                                    <input type="search" placeholder="Search..." class="form-control" v-model="SearchPhrase">
                                    <span class="input-group-append">
                                        <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                    </span>
                                </div>
                                <div style="max-height: 500px; height: 500px; overflow-y: auto;" class="mt-3">
                                    <div v-for="s in Sections" :key="s.Name">
                                        <h6 class="mt-3 mb-1">{{s.Name}}</h6>
                                        <div v-for="f in s.Fields" :key="f.Id">
                                            <a class="dropdown-item" @click="ToggleColumnCommand(f.Id)">
                                                <i class="fa-regular fa-lg mr-1" :class="f.IsSelected ? 'fa-square-check' : 'fa-square'"></i>
                                                <span :title="f.Title">{{f.TitleEllipsis}}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div class="col-md-6">
                                <h5>Selected columns ({{SelectedFields.length}}):</h5>
                                <div style="max-height: 500px; height: 500px; overflow-y: auto;">
                                    <div v-for="f in SelectedFields" :key="f.Id" class="p-2 bg-secondary m-2 text-white">
                                        <span :title="f.Title">{{f.TitleEllipsis}}</span>
                                        <button type="button" class="p-0 border-0 text-white" style="background-color: transparent; font-size: 1.3rem; float: right;"
                                                @click="RemoveColumnCommand(f.Id)">
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                        <button type="button" @click="confirm" class="btn btn-primary">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>